/**
 * Editable Dropdown
 *
 * @example
 *  {{=tmpl['common.dropdown.editable']({
 *      name: 'testLimit',
 *      validationType: 'myLimit',
 *      mode: '',
 *      value: 31337,
 *      label: 'Test Limit',
 *      hint: 'till 12-20-2018 12:00'
 *      placeholder: 'Please, specify test limit',
 *      showCurrencySymbol: true,
 *      dataList: [
 *          {
 *              value: 0,
 *              displayValue: 'None'
 *          },
 *          100,
 *          200,
 *          300,
 *          31337
 *      ],
 *      displayValuesMap: {
 *          '0': '',
 *          '31337': 'Unlimited'
 *      },
 *      dataKey: 'data-attribute-key',
 *      dataValue: 'dataAttrValue',
 *      inputMode: 'decimal'
 *  })}}
 *
 * `validationType` - name of validation to pick from `user.validation.config.js`
 * `mode` - list | text | <anything>. Operating mode. Anything besides 'text|list' means default mode - text+list.
 * `value` - initial (default) value
 * `dataList` - list of suggestions shown in dropdown. Can be either a value type or an object with 'value' and
 *      'displayValue' fields. 'displayValue' from this list takes precedence over 'displayValuesMap'. If a value type
 *      is provided - it appears in dropdown with displayValue taken from 'displayValuesMap'.
 * `displayValuesMap` - map of values which determines what custom value to show in input instead of actual value. This
 *      applies to dropdown list too. Also, backward conversion works too: if you type 'Unlimited' in text input (from
 *      example above) it will be treated as 31337 value.
 *
 * Clearly, you should provide some validation config for validation to work.
 */
define(function(require, exports, module) {
    'use strict';

    var _ = require('underscore');
    var BaseView = require('common/base.view');
    var Model = require('backbone').Model;

    module.exports = BaseView.extend({
        tagName: 'div',

        options: {
            template: 'dropdown.editable',
            selectOptionsTemplate: 'dropdown.editable.select-options',

            /**
             * Operating mode
             * text|list|*
             * Anything besides 'text|list' means default mode - text+list
             */
            mode: null,

            disabled: false,

            label: '',
            placeholder: '',

            /**
             * List of suggestions
             * Array<Object|String>
             * [{ value: '31337', displayValue: 'Unlimited' }]
             */
            dataList: [],

            displayValuesMap: {},
            _displayValuesMapInverted: null
        },

        events: {
            'change .fn-input-text': 'onInputTextChange',
            'change .fn-select': 'onSelectChange'
        },

        initialize: function(options) {
            this.options = _.extend({}, this.options, options);

            this.model = new Model({
                value: this.options.value != null ? this.options.value : ''
            });

            this.setDataList(this.options);
            this.setDisplayValuesMap(this.options);

            this.options._enableTextInput = this.options.mode !== 'list';
            this.options._enableSelect = this.options.mode !== 'text';

            this.el.classList.toggle('list', this.options._enableSelect);
        },

        subscribe: function() {
            this.$el.on('change:options', this.onOptionsChange.bind(this));

            this.listenTo(this.model, 'change:value', this.onModelValueChange.bind(this));
        },

        //region Events

        onInputTextChange: function(e) {
            this.setModelValueFromInput(e);
        },

        onSelectChange: function(e) {
            this.setModelValueFromInput(e);
        },

        onModelValueChange: function() {
            this.updateInputValues();
        },

        onOptionsChange: function(e) {
            var options = e.payload;
            if (!options) {
                return;
            }

            if ('value' in options) {
                this.model.set('value', options.value);
            }

            if ('dataList' in options) {
                this.setDataList(options);
            }

            if ('displayValuesMap' in options) {
                this.setDisplayValuesMap(options);
            }

            if ('disabled' in options) {
                this.setDisabled(options);
            }

            this.updateSelectOptions();
            this.updateInputValues();
        },

        //endregion

        setModelValueFromInput: function(e) {
            var value = e.target.value;
            var isSame = value === this.model.get('value');
            this.model.set('value', this.getValueFromDisplayValue(value));

            if (isSame) {
                this.model.trigger('change:value');
            }
        },

        setDataList: function(options) {
            this.options.dataList = _.map(options.dataList, function(item) {
                if (typeof item !== 'object') {
                    item = {
                        displayValue: this.getDisplayValue(item),
                        value: item
                    };
                }

                return item;
            }.bind(this));
        },

        setDisplayValuesMap: function(options) {
            this.options.displayValuesMap = options.displayValuesMap;
            this.options._displayValuesMapInverted = _.invert(this.options.displayValuesMap);
        },

        getDisplayValue: function(value) {
            return value in this.options.displayValuesMap
                ? this.options.displayValuesMap[value]
                : value;
        },

        getValueFromDisplayValue: function(displayValue) {
            displayValue = displayValue.trim();
            return displayValue in this.options._displayValuesMapInverted
                ? this.options._displayValuesMapInverted[displayValue]
                : displayValue;
        },

        updateSelectOptions: function() {
            this.$select.html(this.tmpl(this.options.selectOptionsTemplate, this.getRenderOptions()));
        },

        updateInputValues: function() {
            var value = this.model.get('value');
            var valueToSend = value;

            if (this.options.inputMode === 'decimal') {
                valueToSend = String(value).replace(',', '.');
            }

            this.$value.val(valueToSend);
            this.$input.val(this.getDisplayValue(value));
            this.$select.val(this.getDisplayValue(value));
        },

        setDisabled: function(options) {
            this.$el.find('input, select').forEach(function(el) {
                el.disabled = options.disabled;
            });
        },

        getRenderOptions: function() {
            return _.extend({}, this.options, {
                inputTextValue: this.getDisplayValue(this.model.get('value'))
            });
        },

        render: function() {
            this.$el.html(this.tmpl(this.options.template, this.getRenderOptions()));

            this.$value = this.$('.fn-input-hidden');
            this.$input = this.$('.fn-input-text');
            this.$select = this.$('.fn-select');
            this.$select.val(this.model.get('value'));

            return this;
        }
    });
});
